import React, { useRef, useMemo, useEffect, useState, useContext, Suspense } from 'react'
import { useFrame, useThree, useLoader } from 'react-three-fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import * as THREE from 'three'
import { draco, OrbitControls } from 'drei'
import {AppContext} from './index'

function useWobble(factor = 1, fn = 'sin', cb) {
  const ref = useRef()
  useFrame((state) => {
    const t = state.clock.getElapsedTime()
    ref.current.position.y = Math[fn](t) * factor
    if (cb) cb(t, ref.current)
  })
  return ref
}

export function BasicModel({ url, cb, ...props }) {
  // const ref = useWobble(0.2, 'sin')
  useFrame((state,delta) => {
    const t = state.clock.getElapsedTime()
    if (cb) cb(t, ref.current)
  })
  const ref = useRef()
  const { scene } = useLoader(GLTFLoader, url, draco());

  return (
    <primitive ref={ref} object={scene} dispose={null} {...props}/>
  )
}

export const Stocking = ({speed, factor, url, ...props}) => {
  const cb = (t,ref) => {
    if (ref) {
      ref.rotation.x = ref.rotation.y += 0.01
    }
  }
  return (
    <group dispose={null}>
      <BasicModel url={'/christmas_stocking/scene.gltf'} {...props}/>
    </group>)
}


export const Gift = ({speed, factor, url, ...props}) => {
  const cb = (t,ref) => {
    if (ref) {
      ref.rotation.y += 0.005
    }
  }
  return (
    <group dispose={null}>
      <BasicModel url={'/gift/scene.gltf'} {...props} cb={cb}/>
    </group>)
}

export const ChristmasTree = ({speed, factor, url, ...props}) => {
  const cb = (t,ref) => {
    if (ref) {
      ref.rotation.y += 0.003
    }
  }
  return (
    <group dispose={null}>
      <BasicModel url={'/christmas_tree_polycraft/scene.gltf'} {...props}/>
    </group>)
}

export function Box(props) {
  const [hovered, set] = useState(false)
  const ref = useWobble(0.5, 'cos')
  useFrame(() => (ref.current.rotation.x = ref.current.rotation.y += 0.01))
  return (
    <mesh ref={ref} {...props} onPointerOver={() => set(true)} onPointerOut={() => set(false)}>
      <boxBufferGeometry attach="geometry" />
      <meshStandardMaterial attach="material" color={'black'} />
    </mesh>
  )
}
export const Plane = ({speed, factor, url, ...props}) => {
  const { animations } = useLoader(GLTFLoader, url)
  const group = useRef()

  const cb = (t, ref) => {
    ref.rotation.x += Math['sin'](t) * .10
  }
  const [mixer] = useState(() => new THREE.AnimationMixer())


  useEffect(() => void mixer.clipAction(animations[0], group.current).play(), [])
  useFrame((state, delta) => {
    mixer.update(delta * speed)

    if  (group.current) {
      group.current.position.x += .03
      if (group.current.position.x >= 48) {
        group.current.position.x = -10.1
      }
    }
  })




  return (
    <group ref={group} dispose={null}>
      <BasicModel url={url} {...props} cb={cb}/>
    </group>)
}
export function Shapes() {
  const {
    viewport: { width, height }
  } = useThree()
  const ringSize = Math.max(3, width / 2)
  const crossSize = 0.7
  return (
    <>
      <OrbitControls/>
      <Ring position={[-width * 0.8, height * -3, -5]} scale={[ringSize, ringSize, 1]} />
      <Cross position={[-width / 2.5, height / 8, -1]} scale={[crossSize, crossSize, 1]} rotation={[0, 0, Math.PI / 4]} />
      <Minus position={[width / 3, -height / 3.5, -2]} scale={[0.8, 0.8, 0.8]} rotation={[0, 0, Math.PI / 10]} />
      <group rotation={[Math.PI / 8, 0, 0]} position={[-width / 4, -height / 6, 0]}>
        <Suspense fallback={null}>
          <Gift scale={[0.001,0.001,0.001]}/>
        </Suspense>
        <Suspense fallback={null}>
          <Stocking scale={[0.01,0.01,0.01]} position={[0, 0, -3]}/>
        </Suspense>
        <Box position={[width / 1.5, height / 4, -3]} scale={[0.5, 0.5, 0.5]} />
        <Lights />
        <Suspense fallback={null}>
        <Plane url={'/cartoon_plane/scene.gltf'} scale={[0.0017,0.0017,0.0017]} speed={1} factor={0.6} position={[-20.1,-3.5,-20]} rotation={[0, Math.PI/2,0]}/>
        </Suspense>
      </group>
    </>
  )
}

function Ring(props) {
  return (
    <mesh {...props}>
      <ringBufferGeometry attach="geometry" args={[1, 1.4, 64]} />
      <meshBasicMaterial attach="material" color="white" opacity={1} toneMapped={false} />
    </mesh>
  )
}

function Cross(props) {
  const inner = useRef()
  const ref = useWobble(0.1, 'sin', () => (inner.current.rotation.z += 0.001))
  return (
    <group ref={ref}>
      <group ref={inner} {...props}>
        <mesh>
          <planeBufferGeometry attach="geometry" args={[2, 0.25]} />
          <meshBasicMaterial attach="material" color="#FFEDDD" toneMapped={false} />
        </mesh>
        <mesh position={[0, -0.625, 0]}>
          <planeBufferGeometry attach="geometry" args={[0.25, 0.75]} />
          <meshBasicMaterial attach="material" color="#FFEDDD" toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.625, 0]}>
          <planeBufferGeometry attach="geometry" args={[0.25, 0.75]} />
          <meshBasicMaterial attach="material" color="#FFEDDD" toneMapped={false} />
        </mesh>
      </group>
    </group>
  )
}

function Minus(props) {
  const ref = useWobble(0.1, 'sin')
  return (
    <group ref={ref}>
      <group {...props}>
        <mesh>
          <planeBufferGeometry attach="geometry" args={[2, 0.7]} />
          <meshBasicMaterial attach="material" color="#DEF5FF" toneMapped={false} transparent opacity={0.7} />
        </mesh>
      </group>
    </group>
  )
}

function Lights() {
  return (
    <>
      <ambientLight intensity={0.2} />
      <pointLight color={'black'} position={[7, -5, 10]} intensity={2} angle={0.3} penumbra={1} />
      <pointLight position={[1, -1, -5]} intensity={2} />
    </>
  )
}

export function Categories({ time = 3000 }) {

  const [index, set] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => {
      set((index + 1))
      index >= cats.length - 1 ? set(0):set(index+1)
    }, time)
    return () => clearInterval(interval)
  }, [index])



  const cats = useMemo(
    () => [
     'Games', 'Jewelry', 'Candy', 'Charity', 'Snacks', 'Movies', 'Books', 'Clothes', 'Makeup', 'Gift Cards', 'Gadgets',
    ],
    []
  )

  const ref = useRef()
  useEffect(() => {
    ref.current.style.animation = 'none'
    void ref.current.offsetHeight
    ref.current.style.animation = `changewidth ${time / 1000}s linear`
  }, [index])

  return (
    <p style={{ height: 70, textAlign: 'center' }}>
      <a href="#" style={{ width: '190px' }} onClick={() => index >= cats.length - 1 ? set(0):set(index+1)}>
        <div
          ref={ref}
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 1.5,
            opacity: 0.5,
            background: 'white'
          }}
        />.
        {cats.map(( description , i) => (
          <span key={i} hidden={i !== index || undefined} className="transition horizontal" style={{textAlign: 'center',width: '100%', left: 0, height: '100%', color: 'white' }}>
          {description}
        </span>
        ))}
      </a>

    </p>
  )
}
